import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Sparkles, Zap, AlertTriangle, CheckCircle2 } from "lucide-react";

/** Live progress view for the massive Mexal import pipeline (Phase 6). */
export default function AdminMexalImport() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [retryingN, setRetryingN] = useState(false);
    const [retryingE, setRetryingE] = useState(false);
    const [errListKind, setErrListKind] = useState(null);
    const [errList, setErrList] = useState([]);

    const load = () => {
        api.get("/admin/mexal/import-progress")
            .then(({ data }) => setData(data))
            .catch(() => toast.error("Errore caricamento progresso"))
            .finally(() => setLoading(false));
    };
    useEffect(() => {
        load();
        const t = setInterval(load, 8000);
        return () => clearInterval(t);
    }, []);

    const retryQueue = async (kind) => {
        const setter = kind === "normalize" ? setRetryingN : setRetryingE;
        setter(true);
        try {
            const { data } = await api.post(`/admin/mexal/queue-retry?kind=${kind}`);
            toast.success(`Riaccodati ${data.requeued} elementi`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally { setter(false); }
    };

    const showErrors = async (kind) => {
        setErrListKind(kind);
        try {
            const { data } = await api.get(`/admin/mexal/queue-errors?kind=${kind}`);
            setErrList(data.items || []);
        } catch { toast.error("Errore caricamento errori"); }
    };

    if (loading || !data) {
        return <div className="text-[hsl(var(--muted-foreground))]">Caricamento…</div>;
    }

    const total = data.total_mexal_wines || 0;
    const nPct = total ? Math.round((data.normalized / total) * 100) : 0;
    const ePct = total ? Math.round((data.enriched / total) * 100) : 0;

    return (
        <div data-testid="admin-mexal-import-page">
            <div className="flex items-baseline justify-between mb-6 gap-4">
                <div>
                    <h1 className="font-serif text-3xl mb-2">Import Mexal — pipeline</h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
                        I vini creati dall'import Mexal partono con la sola descrizione grezza e
                        vengono poi <b>normalizzati</b> (nome/produttore/annata) e <b>arricchiti</b>
                        (note, abbinamenti) in background da 2 code persistenti. Solo dopo la
                        normalizzazione il vino diventa visibile al pubblico.
                    </p>
                </div>
                <button onClick={load} className="co-btn-ghost text-sm" data-testid="mexal-import-refresh">
                    <RefreshCw className="w-3.5 h-3.5" /> Aggiorna
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <Stat label="Vini Mexal" value={total} testid="mexal-total" />
                <Stat label="Normalizzati" value={`${data.normalized} · ${nPct}%`} testid="mexal-normalized" />
                <Stat label="Arricchiti" value={`${data.enriched} · ${ePct}%`} testid="mexal-enriched" />
                <Stat label="Validati" value={data.validated} testid="mexal-validated" />
            </div>

            <div className="grid gap-3 md:grid-cols-2 mb-6">
                <QueuePanel
                    title="Coda normalizzazione"
                    icon={Zap}
                    pending={data.normalize_pending}
                    errors={data.normalize_errors}
                    total={total}
                    processed={data.normalized}
                    onRetry={() => retryQueue("normalize")}
                    onShowErrors={() => showErrors("normalize")}
                    retrying={retryingN}
                    testidPrefix="mexal-normalize-queue"
                />
                <QueuePanel
                    title="Coda arricchimento"
                    icon={Sparkles}
                    pending={data.enrich_pending}
                    errors={data.enrich_errors}
                    total={data.normalized || total}
                    processed={data.enriched}
                    onRetry={() => retryQueue("enrich")}
                    onShowErrors={() => showErrors("enrich")}
                    retrying={retryingE}
                    testidPrefix="mexal-enrich-queue"
                />
            </div>

            <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-4 mb-6">
                <div className="text-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} />
                        <b>Tempo stimato di completamento</b>
                    </div>
                    <div className="text-[hsl(var(--muted-foreground))]">
                        Con throttle AI cliente 15/min + fallback piattaforma, i worker processano
                        ~5 elementi/minuto per coda.
                        <br />
                        Stima residua: <b className="text-[hsl(var(--foreground))]" data-testid="mexal-eta-minutes">
                            {data.eta_minutes != null ? `${data.eta_minutes} min` : "n/d"}
                        </b>
                        {data.eta_minutes != null && data.eta_minutes > 60 && (
                            <span> ({Math.round(data.eta_minutes / 60 * 10) / 10} h)</span>
                        )}
                    </div>
                </div>
            </div>

            {data.last_run && (
                <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-4" data-testid="mexal-last-run">
                    <div className="text-sm">
                        <b>Ultima esecuzione sync:</b>{" "}
                        {data.last_run.status} · scansionati {data.last_run.scanned}
                        {" · "}stock aggiornati {data.last_run.updated_stock || 0}
                        {" · "}nuovi creati {data.last_run.new_created || 0}
                        {data.last_run.skipped_zero_stock ? ` · saltati g.zero ${data.last_run.skipped_zero_stock}` : ""}
                        {data.last_run.capped ? ` · esclusi da cap ${data.last_run.capped}` : ""}
                    </div>
                </div>
            )}

            {errListKind && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setErrListKind(null)}>
                    <div className="bg-white rounded-sm max-w-2xl w-full max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="mexal-errors-modal">
                        <h2 className="text-xl mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-[hsl(var(--destructive))]" strokeWidth={1.5} />
                            Errori coda {errListKind === "normalize" ? "normalizzazione" : "arricchimento"}
                        </h2>
                        {errList.length === 0 ? (
                            <div className="text-sm text-[hsl(var(--muted-foreground))]">Nessun errore.</div>
                        ) : (
                            <div className="space-y-2 text-xs">
                                {errList.map((it) => (
                                    <div key={it.id} className="p-2 bg-red-50 border border-red-200 rounded-sm">
                                        <div className="font-mono">wine_id: {it.wine_id}{it.sku ? ` · sku ${it.sku}` : ""}</div>
                                        <div className="text-[hsl(var(--destructive))] mt-1 break-all">{it.last_error}</div>
                                        <div className="text-[hsl(var(--muted-foreground))] mt-1">tentativi: {it.attempts}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="mt-4 flex justify-end">
                            <button onClick={() => setErrListKind(null)} className="co-btn-ghost text-sm">Chiudi</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, testid }) {
    return (
        <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-4" data-testid={testid}>
            <div className="co-caption text-xs">{label}</div>
            <div className="text-2xl mt-1">{value}</div>
        </div>
    );
}

function QueuePanel({ title, icon: Icon, pending, errors, total, processed, onRetry, onShowErrors, retrying, testidPrefix }) {
    const pct = total ? Math.round((processed / total) * 100) : 0;
    return (
        <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-4" data-testid={`${testidPrefix}-panel`}>
            <div className="flex items-center gap-2 mb-3">
                <Icon className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} />
                <h3 className="text-lg">{title}</h3>
            </div>
            <div className="text-sm space-y-1">
                <div className="flex justify-between"><span>Processati</span><b data-testid={`${testidPrefix}-processed`}>{processed} / {total}</b></div>
                <div className="flex justify-between"><span>In coda</span><b data-testid={`${testidPrefix}-pending`}>{pending}</b></div>
                <div className="flex justify-between text-[hsl(var(--destructive))]"><span>Errori (max retry)</span><b data-testid={`${testidPrefix}-errors`}>{errors}</b></div>
            </div>
            <div className="mt-2 h-1 bg-[hsl(var(--secondary))] rounded-full overflow-hidden">
                <div className="h-full bg-[hsl(var(--primary))]" style={{ width: `${pct}%` }} />
            </div>
            {errors > 0 && (
                <div className="mt-3 flex gap-2">
                    <button onClick={onShowErrors} className="co-btn-ghost text-xs" data-testid={`${testidPrefix}-show-errors`}>
                        Vedi errori
                    </button>
                    <button onClick={onRetry} disabled={retrying} className="co-btn-ghost text-xs" data-testid={`${testidPrefix}-retry`}>
                        {retrying ? "…" : "Riaccoda"}
                    </button>
                </div>
            )}
        </div>
    );
}
